import * as THREE from 'three';
import { satchelVariant } from '../../../content/maps/prop-variants.js';
import { groundDecal } from '../environment/ground-decal.js';
import { createSettlementProp } from '../environment/settlement-props.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import { conformTerrainDecal } from '../environment/terrain-decals.js';
import { PROP_RADIUS } from '../../../shared/game/prop-footprints.js';
import type { GameContent, MapProp } from '../../../shared/game/world-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';

export interface SupplyHandlers { loot(containerId: string): void; merchant(merchantId: string): void; read(containerId: string): void; use(propId: string): void }
/** `hoverLabel`: the label shows only while the marker is hovered or TAB is held. */
interface Marker { group: THREE.Group; label: HTMLDivElement | null; height: number; glow: THREE.Mesh; pulse: boolean; outline: THREE.Object3D[]; bodies: THREE.Mesh[]; activate: (() => void) | null; hoverLabel?: boolean }

/** How tall a usable prop's unseen click column stands, by the kind drawing it (default 3 m). */
const USE_HEIGHT: Partial<Record<MapProp['kind'], number>> = { lighthouse: 27 };

const outlineMaterial = new THREE.LineBasicMaterial({ color: 0xf5d39c, depthTest: false, transparent: true });

function glowRing(radius: number) {
  const glow = groundDecal(new THREE.Mesh(new THREE.RingGeometry(radius - .07, radius, 40), new THREE.MeshBasicMaterial({ color: 0xe4b56c, transparent: true, opacity: .55, side: THREE.DoubleSide })));
  glow.rotation.x = -Math.PI / 2; glow.position.y = .06; return glow;
}
/** Edge outlines of a model's meshes, shown only while TAB is held; returns the outlines and the meshes that can be clicked. */
function outlineOf(group: THREE.Group) {
  const bodies: THREE.Mesh[] = []; group.traverse(child => { if (child instanceof THREE.Mesh) bodies.push(child); });
  const outline = bodies.map(mesh => { const edges = new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry, 30), outlineMaterial); edges.renderOrder = 10; edges.visible = false; mesh.add(edges); return edges; });
  return { bodies, outline };
}
/** The same models the prop viewer reviews, drawn at the origin of a scratch parent and lifted out so the marker owns the group. */
function propModel(kind: 'chest' | 'satchel' | 'potion-stall', x: number, z: number, variant = 0): THREE.Group {
  const parent = new THREE.Group(); createSettlementProp(parent, { kind, x, z, variant });
  const group = parent.children[0] as THREE.Group; group.removeFromParent(); return group;
}

/** An unseen column that takes clicks, standing on the marker's ground at an offset. */
function grabColumn(radius: number, height: number, x = 0, z = 0) {
  const grab = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 12), new THREE.MeshBasicMaterial()); grab.position.set(x, height / 2, z); grab.visible = false; return grab;
}

/** Presentation only: chests, satchels and merchant stalls exist where the server publishes them; usable props offer their next use. */
export class SupplyMarkers {
  private containers = new Map<string, Marker>();
  private merchants = new Map<string, Marker>();
  private places = new Map<string, Marker>();
  private usable = new Map<string, Marker>();
  private highlight = false;
  private hovered: Marker | null = null;
  constructor(private scene: THREE.Scene, private host: HTMLElement, private content: GameContent, private handlers: SupplyHandlers) {
    for (const merchant of content.merchants ?? []) {
      const group = propModel('potion-stall', merchant.x, merchant.z); group.visible = false;
      group.position.y = terrainHeight(content.map, merchant.x, merchant.z);
      const { bodies, outline } = outlineOf(group);
      const glow = glowRing(1.6); glow.visible = false; group.add(glow); this.scene.add(group);
      this.merchants.set(merchant.id, { group, label: null, height: 2.7, glow, pulse: true, outline, bodies, activate: () => this.handlers.merchant(merchant.id) });
    }
    for (const place of content.safePlaces ?? []) {
      // A calm green ring on the ground: where a Safe Rest is possible. Not interactive.
      const group = new THREE.Group(); group.position.set(place.x, terrainHeight(content.map, place.x, place.z), place.z); group.visible = false;
      const ring = groundDecal(new THREE.Mesh(new THREE.RingGeometry(place.radius - .25, place.radius, 64), new THREE.MeshBasicMaterial({ color: 0x9ed58c, transparent: true, opacity: .45, side: THREE.DoubleSide })));
      ring.rotation.x = -Math.PI / 2; ring.position.y = .07; group.add(ring); this.scene.add(group);
      const label = this.label(`Safe place · ${place.name}`, () => {}); label.classList.add('safe-place-label'); label.style.pointerEvents = 'none'; label.removeAttribute('role'); label.tabIndex = -1;
      this.places.set(place.id, { group, label, height: .6, glow: ring, pulse: false, outline: [], bodies: [], activate: null });
    }
    for (const prop of content.usableProps ?? []) {
      // A ring where the hero stands to use it; the whole prop drawing it takes the click as well as that spot.
      const group = new THREE.Group(); group.position.set(prop.x, terrainHeight(content.map, prop.x, prop.z), prop.z); group.visible = false;
      const glow = glowRing(prop.radius + .4); glow.visible = false; group.add(glow);
      const drawn = content.map.scenery?.props.find(candidate => candidate.id === prop.id), bodies = [grabColumn(prop.radius + .4, 1.8)];
      if (drawn) bodies.push(grabColumn(PROP_RADIUS[drawn.kind] * (drawn.scale ?? 1), USE_HEIGHT[drawn.kind] ?? 3, drawn.x - prop.x, drawn.z - prop.z));
      group.add(...bodies); this.scene.add(group);
      const activate = () => this.handlers.use(prop.id);
      this.usable.set(prop.id, { group, label: this.label(prop.name, activate), height: 2.4, glow, pulse: true, outline: [], bodies, activate, hoverLabel: true });
    }
  }
  private label(text: string, activate: () => void) {
    const label = document.createElement('div'); label.className = 'world-label supply-label'; label.textContent = text; label.hidden = true;
    label.setAttribute('role', 'button'); label.tabIndex = 0;
    label.addEventListener('click', event => { event.stopPropagation(); activate(); });
    label.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); activate(); } });
    this.host.append(label); return label;
  }
  receive(snapshot: WorldSnapshot) {
    const live = new Set(snapshot.containers.map(container => container.id));
    for (const [id, marker] of this.containers) if (!live.has(id)) { this.remove(marker); this.containers.delete(id); }
    for (const container of snapshot.containers) {
      let marker = this.containers.get(container.id);
      if (!marker) {
        const group = propModel(container.id.startsWith('spoils-') || container.readableId ? 'satchel' : 'chest', container.x, container.z, satchelVariant(container.id));
        const { bodies, outline } = outlineOf(group);
        // A satchel is a hand-sized mesh: an unseen, generous cylinder makes it as easy to click as a unit.
        const grab = new THREE.Mesh(new THREE.CylinderGeometry(.6, .6, .9, 12), new THREE.MeshBasicMaterial()); grab.position.y = .45; grab.visible = false; group.add(grab); bodies.push(grab);
        const glow = glowRing(.8); glow.visible = false; group.add(glow); this.scene.add(group);
        marker = { group, label: null, height: 1.3, glow, pulse: true, outline, bodies, activate: () => container.readableId ? this.handlers.read(container.id) : this.handlers.loot(container.id) }; this.containers.set(container.id, marker);
      }
      marker.group.visible = true;
      marker.group.position.y = terrainHeight(this.content.map, container.x, container.z);
    }
    const visible = new Set(snapshot.merchants.map(merchant => merchant.id));
    for (const [id, marker] of this.merchants) marker.group.visible = visible.has(id);
    for (const place of this.content.safePlaces ?? []) {
      const near = snapshot.fog.sources.some(source => Math.hypot(source.x - place.x, source.z - place.z) <= snapshot.fog.visionRadius + place.radius);
      this.places.get(place.id)!.group.visible = near;
    }
    for (const prop of this.content.usableProps ?? []) {
      const marker = this.usable.get(prop.id)!, state = snapshot.props[prop.id] ?? prop.initial;
      marker.group.visible = !!snapshot.fog.revealed || snapshot.fog.sources.some(source => Math.hypot(source.x - prop.x, source.z - prop.z) <= snapshot.fog.visionRadius + prop.radius);
      marker.label!.textContent = prop.uses.find(use => use.from === state)?.label ?? prop.name;
    }
  }

  /** Holding TAB outlines everything that can be interacted with. */
  setHighlight(on: boolean) { this.highlight = on; }
  private nearest(raycaster: THREE.Raycaster): Marker | null {
    let best: { distance: number; marker: Marker } | null = null;
    for (const marker of [...this.containers.values(), ...this.merchants.values(), ...this.usable.values()]) {
      if (!marker.group.visible || !marker.activate) continue;
      const hit = raycaster.intersectObjects(marker.bodies, false)[0];
      if (hit && (!best || hit.distance < best.distance)) best = { distance: hit.distance, marker };
    }
    return best?.marker ?? null;
  }
  /** The interaction under the ray, if an interactable is the nearest thing it strikes. */
  pick(raycaster: THREE.Raycaster): (() => void) | null { return this.nearest(raycaster)?.activate ?? null; }
  /** Outlines the interactable under the ray (null clears); returns whether one is hovered. */
  hover(raycaster: THREE.Raycaster | null): boolean { this.hovered = raycaster ? this.nearest(raycaster) : null; return this.hovered !== null; }

  render(camera: THREE.Camera, now: number, viewport: { width: number; height: number }) {
    const pulse = .6 + Math.sin(now / 420) * .08;
    for (const marker of this.all()) {
      if (!marker.label) {
        const shown = this.highlight || marker === this.hovered; marker.glow.visible = shown; for (const edges of marker.outline) edges.visible = shown;
        if (marker.pulse) marker.glow.scale.setScalar(pulse + .4);
        conformTerrainDecal(marker.glow, this.content.map);
        continue;
      }
      if (!marker.group.visible) { marker.label.hidden = true; continue; }
      if (marker.hoverLabel) {
        const shown = this.highlight || marker === this.hovered; marker.glow.visible = shown; marker.glow.scale.setScalar(pulse + .4);
        if (!shown) { marker.label.hidden = true; continue; }
      }
      conformTerrainDecal(marker.glow, this.content.map);
      const point = marker.group.position.clone().add(new THREE.Vector3(0, marker.height, 0)).project(camera);
      marker.label.hidden = !marker.group.visible || point.z < -1 || point.z > 1 || Math.abs(point.x) > 1.05 || Math.abs(point.y) > 1.05;
      if (!marker.label.hidden) marker.label.style.transform = `translate(-50%, -50%) translate(${(point.x + 1) / 2 * viewport.width}px, ${(-point.y + 1) / 2 * viewport.height}px)`;
    }
  }

  private remove(marker: Marker) { this.scene.remove(marker.group); marker.label?.remove(); }
  private all() { return [...this.containers.values(), ...this.merchants.values(), ...this.places.values(), ...this.usable.values()]; }
  clear() {
    for (const marker of this.all()) { marker.group.visible = false; if (marker.label) marker.label.hidden = true; }
  }
  dispose() {
    for (const marker of this.all()) this.remove(marker);
    this.containers.clear(); this.merchants.clear(); this.places.clear(); this.usable.clear();
  }
}
